import * as React from 'react';
import { useState, useCallback, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { useIsFirstRender } from '../hooks/useIsFirstRender';
import { getChoosePlanNewData } from '../helpers/helpers';
import { BoostEverywhereResponse } from '../responses/BoostEverywhereResponse';
import CustomDemoVideo from '../components/BoostV2/interface/CustomDemoVideo';
import { Modal } from '../components/modals/Modal';
import { useWindowWidth } from '../hooks/useWindowWidth';

const BoostEverywhereAdChooserPage = lazy(() => import('../pages/BoostEverywhereAdChooserPage'));
const NoListingPageAndHeader = lazy(() => import('./NoListingPageAndHeader'));
const NonPartnerNoListingsPage = lazy(() => import('./NonPartnerNoListingsPage'));
const MainPage = lazy(() => import('./MainPage'));
const SelectPage = lazy(() => import('./SelectPage'));

const App = (): JSX.Element => {
    const [data, setData] = useState(null);
    const [preCart, setPreCart] = useState(null);
    const isFirstRender = useIsFirstRender();
    const [basePromoAdUrl, setBasePromoAdUrl] = useState(null);
    const [baseListingAdUrl, setBaseListingAdUrl] = useState(null);
    const [listings, setListings] = useState([]);
    const [sendToBoostEverywhere, setSendToBoostEverywhere] = useState(true);
    const [integrationRequestedListingCampaign, setIntegrationRequestedListingCampaign] =
        useState(false);
    const [dashboardLinkUrl, setDashboardLinkUrl] = useState(null);
    const [oneListing, setOneListing] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [partnerAgentID, setPartnerAgentID] = useState(null);
    const [partner, setPartner] = useState(null);
    const [retailPromoBasePrice, setRetailPromoBasePrice] = useState(129);
    const [retailListingBasePrice, setRetailListingBasePrice] = useState(69);

    const widerThan420 = useWindowWidth(420);
    const partnerSlug = preCart?.partner?.length > 0 ? preCart?.partner : 'null';
    const getBackendData = async (): Promise<void> => {
        const response = await getChoosePlanNewData();
        if (response) setData(response as BoostEverywhereResponse);
    };

    const openModal = useCallback((): void => {
        setModalOpen(!modalOpen);
    }, [modalOpen]);

    useEffect(() => {
        if (isFirstRender) getBackendData();
    }, [isFirstRender]);

    useEffect(() => {
        if (data) {
            setPreCart(data.preCart);
            setBasePromoAdUrl(data.basePromoAdUrl);
            setBaseListingAdUrl(data.baseListingAdUrl);
            setListings(data.listings);
            setSendToBoostEverywhere(data.sendToBoostEverywhere);
            setIntegrationRequestedListingCampaign(data.integrationRequestedListingCampaign);
            setRetailPromoBasePrice(data.retailPromoBasePrice);
            setRetailListingBasePrice(data.retailListingBasePrice);
            setDashboardLinkUrl(data.dashboardLinkUrl);
            setPartner(data.preCart.partner);
            setPartnerAgentID(data.preCart.partner_agent_id);
        }
    }, [data]);

    useEffect(() => {
        if (listings && listings.length === 1) {
            setOneListing(listings[0]);
        }
    }, [listings]);

    const VideoModal = useCallback(
        () => (
            <Modal
                isOpen={modalOpen}
                toggle={() => openModal()}
                id='boostEverywhere'
                videoOverride
                fullScreen={!widerThan420}
            >
                <CustomDemoVideo />
            </Modal>
        ),
        [modalOpen, openModal, widerThan420]
    );

    const redirectComponents = (): JSX.Element => {
        if (listings && listings.length > 1) {
            return (
                <SelectPage
                    preCart={preCart}
                    listings={listings}
                    dashboardLinkUrl={dashboardLinkUrl}
                    sendToBoostEverywhere={sendToBoostEverywhere}
                />
            );
        }
        if (
            listings.length < 1 &&
            partnerAgentID &&
            partner &&
            integrationRequestedListingCampaign
        ) {
            return <NoListingPageAndHeader preCart={preCart} baseListingAdUrl={baseListingAdUrl} />;
        }

        if (preCart?.landing_page_variant === 'ad-chooser-expanded') {
            return (
                <BoostEverywhereAdChooserPage
                    preCart={preCart}
                    basePromoAdUrl={basePromoAdUrl}
                    baseListingAdUrl={baseListingAdUrl}
                    openModal={openModal}
                    listing={oneListing}
                    collapsed={false}
                    videoModal={VideoModal}
                    retailPromoBasePrice={retailPromoBasePrice}
                />
            );
        }

        if (preCart?.landing_page_variant === 'ad-chooser-collapsed') {
            return (
                <BoostEverywhereAdChooserPage
                    preCart={preCart}
                    basePromoAdUrl={basePromoAdUrl}
                    baseListingAdUrl={baseListingAdUrl}
                    openModal={openModal}
                    listing={oneListing}
                    collapsed
                    videoModal={VideoModal}
                    retailPromoBasePrice={retailPromoBasePrice}
                />
            );
        }

        return (
            <MainPage
                preCart={preCart}
                openModal={openModal}
                basePromoAdUrl={basePromoAdUrl}
                baseListingAdUrl={baseListingAdUrl}
                oneListing={oneListing}
                VideoModal={VideoModal}
                retailPromoBasePrice={retailPromoBasePrice}
            />
        );
    };

    return (
        <Router>
            <Routes>
                <Route
                    path='/agents/boost/*'
                    element={<Suspense fallback={<>...</>}> {redirectComponents()} </Suspense>}
                />
                <Route
                    path='/agents/boost/home'
                    element={
                        <Suspense fallback={<>...</>}>
                            <MainPage
                                preCart={preCart}
                                openModal={openModal}
                                basePromoAdUrl={basePromoAdUrl}
                                baseListingAdUrl={baseListingAdUrl}
                                oneListing={oneListing}
                                VideoModal={VideoModal}
                                retailPromoBasePrice={retailPromoBasePrice}
                            />
                        </Suspense>
                    }
                />
                <Route
                    path='/agents/boost/multiple'
                    element={
                        <Suspense fallback={<>...</>}>
                            <SelectPage
                                preCart={preCart}
                                listings={listings}
                                dashboardLinkUrl={dashboardLinkUrl}
                                sendToBoostEverywhere={sendToBoostEverywhere}
                            />
                        </Suspense>
                    }
                />
                <Route
                    path='/agents/boost/noListing'
                    element={
                        <Suspense fallback={<>...</>}>
                            <NoListingPageAndHeader
                                preCart={preCart}
                                baseListingAdUrl={baseListingAdUrl}
                            />
                        </Suspense>
                    }
                />
                <Route
                    path={`/agents/nonPartnerNoListings/${partnerSlug}/${preCart?.id}`}
                    element={
                        <Suspense fallback={<>...</>}>
                            <NonPartnerNoListingsPage preCart={preCart} />
                        </Suspense>
                    }
                />
            </Routes>
        </Router>
    );
};

export default App;
